import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, ArrowUp, ArrowDown, ExternalLink, Eye, ImageIcon,
  MoreVertical, Pencil, Plus, Power, Trash2, Upload, Film, ImagePlus,
  Users, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  api, friendlyError,
  adminListVideos, adminCreateVideo, adminUpdateVideo, adminDeleteVideo,
  adminMoveVideo, buildAssetUrl, uploadAdminImage,
} from '@/lib/api';
import { AssignPlayersDialog } from '@/components/admin/AssignPlayersDialog';
import { AdminDownloadAssetDialog } from '@/components/admin/AdminDownloadAssetDialog';
import { AdminVideoGrantsDialog } from '@/components/admin/AdminVideoGrantsDialog';
import statsThumbnail from '@/assets/stats-thumbnail.png';

const CONTENT_KIND = [
  { value: 'highlights', label: 'Highlights' },
  { value: 'full_match', label: 'Partido completo' },
  { value: 'summary', label: 'Resumen' },
  { value: 'interview', label: 'Entrevista' },
  { value: 'statistics', label: 'Estadísticas' },
  { value: 'other', label: 'Otro' },
];

const ACCESS = [
  { value: 'free', label: 'Gratis' },
  { value: 'premium', label: 'Solo Premium' },
  { value: 'pay_per_content', label: 'Solo pago individual' },
  { value: 'premium_or_pay_per_content', label: 'Incluido en Premium o disponible mediante pago individual' },
];

const DOWNLOAD_MODES = [
  { value: 'none', label: 'No permitir descarga' },
  { value: 'included_with_access', label: 'Descarga incluida para cualquier usuario con acceso al contenido' },
  { value: 'included_premium_pay_others', label: 'Descarga incluida para usuarios Premium; disponible mediante pago para usuarios no Premium' },
  { value: 'paid_for_all', label: 'Descarga de pago para cualquier usuario con acceso al contenido' },
];

const SOURCE = [
  { value: 'mp4', label: 'URL MP4' },
  { value: 'hls', label: 'URL HLS (.m3u8)' },
  { value: 'embed', label: 'Embed (YouTube/Vimeo/Dailymotion/Bunny)' },
  { value: 'external_link', label: 'Enlace web externo' },
];

const STATUS_LABEL = {
  draft: 'Borrador', published: 'Publicado', unavailable: 'Desactivado',
  replay_available: 'Publicado', private: 'Borrador',
  processing: 'Procesando', archived: 'Archivado',
};

const STATUS_BADGE_CLASS = {
  draft: 'bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)]',
  published: 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)]',
  replay_available: 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)]',
  unavailable: 'bg-red-50 text-red-600',
};

function eurFromCents(c) {
  if (c === '' || c == null) return '';
  const n = Number(c);
  if (Number.isNaN(n)) return '';
  return (n / 100).toFixed(2).replace('.', ',');
}
function centsFromEurString(s) {
  if (typeof s !== 'string' || !s.trim()) return null;
  const cleaned = s.replace(/\s|€/g, '').replace(/,/g, '.');
  const n = Number(cleaned);
  if (Number.isNaN(n) || n < 0) return null;
  return Math.round(n * 100);
}

function accessLabel(v) {
  switch (v.access_type) {
    case 'free': return 'Gratis';
    case 'premium': return 'Solo Premium';
    case 'pay_per_content': return `Pago individual · ${eurFromCents(v.price_cents)} €`;
    case 'premium_or_pay_per_content': return `Premium o ${eurFromCents(v.price_cents)} €`;
    default: return 'Heredado';
  }
}

function downloadLabel(v) {
  switch (v.download_mode) {
    case 'none': return 'No disponible';
    case 'included_with_access': return 'Incluida';
    case 'included_premium_pay_others':
      return `Incluida para Premium · ${eurFromCents(v.download_price_cents)} € no Premium`;
    case 'paid_for_all': return `Descarga de pago · ${eurFromCents(v.download_price_cents)} €`;
    default: return '—';
  }
}

const EMPTY_FORM = {
  id: null,
  title: '',
  description: '',
  content_kind: 'highlights',
  video_type: 'mp4',
  video_url: '', hls_url: '', embed_url: '', external_url: '',
  thumbnail_url: '',
  access_type: 'free',
  price_eur: '',
  download_mode: 'none',
  download_price_eur: '',
  status: 'draft',
};

export default function AdminEventContents() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [assignVideoId, setAssignVideoId] = useState(null);
  // Fase 06D · gestor de download_assets. Modal parametrizable con source.
  const [downloadTarget, setDownloadTarget] = useState(null); // { sourceType, sourceId, title } | null
  // Fase 1 · concesiones gratuitas de visualización por vídeo.
  const [grantsTarget, setGrantsTarget] = useState(null); // { videoId, videoTitle } | null

  const eventTitle = event?.title || '…';
  const sportLabel = event?.category || '';
  const dateLabel = event?.event_date ? new Date(event.event_date).toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '';

  // Cargar partido + contenidos
  const reload = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const [evRes, vidsRes] = await Promise.all([
        api.get(`/admin/events/${eventId}`),
        adminListVideos({ event_id: eventId, page_size: 50 }),
      ]);
      setEvent(evRes.data?.data || null);
      const list = (vidsRes?.items || []).slice().sort((a, b) => {
        const pa = a.is_primary ? 0 : 1;
        const pb = b.is_primary ? 0 : 1;
        if (pa !== pb) return pa - pb;
        return (a.sort_order || 0) - (b.sort_order || 0);
      });
      setItems(list);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-line */ }, [eventId]);

  const isPriceVisible = useMemo(
    () => form.access_type === 'pay_per_content' || form.access_type === 'premium_or_pay_per_content',
    [form.access_type],
  );
  const isDownloadPriceVisible = useMemo(
    () => form.download_mode === 'included_premium_pay_others' || form.download_mode === 'paid_for_all',
    [form.download_mode],
  );

  const openNew = () => {
    setForm({ ...EMPTY_FORM });
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      title: row.title || '',
      description: row.description || '',
      content_kind: row.content_kind || 'highlights',
      video_type: row.video_type || 'mp4',
      video_url: row.video_url || '',
      hls_url: row.hls_url || '',
      embed_url: row.embed_url || '',
      external_url: row.external_url || '',
      thumbnail_url: row.thumbnail_url || '',
      access_type: row.access_type || 'free',
      price_eur: eurFromCents(row.price_cents),
      download_mode: row.download_mode || 'none',
      download_price_eur: eurFromCents(row.download_price_cents),
      status: row.status === 'replay_available' ? 'published' : (row.status || 'draft'),
    });
    setDrawerOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        event_id: eventId,
        title: form.title.trim(),
        description: form.description?.trim() || null,
        content_kind: form.content_kind,
        video_type: form.video_type,
        video_url: form.video_type === 'mp4' ? (form.video_url || null) : null,
        hls_url: form.video_type === 'hls' ? (form.hls_url || null) : null,
        embed_url: form.video_type === 'embed' ? (form.embed_url || null) : null,
        external_url: form.video_type === 'external_link' ? (form.external_url || null) : null,
        thumbnail_url: form.thumbnail_url || null,
        access_type: form.access_type,
        status: form.status,
        download_mode: form.download_mode,
      };
      if (isPriceVisible) {
        const c = centsFromEurString(form.price_eur);
        if (!c || c <= 0) {
          toast.error('Indica un precio de reproducción válido (p.ej. 2,50).');
          setSaving(false); return;
        }
        payload.price_cents = c;
      } else {
        payload.price_cents = null;
      }
      if (isDownloadPriceVisible) {
        const dc = centsFromEurString(form.download_price_eur);
        if (!dc || dc <= 0) {
          toast.error('Indica un precio de descarga válido (p.ej. 1,50).');
          setSaving(false); return;
        }
        payload.download_price_cents = dc;
      } else {
        payload.download_price_cents = null;
      }

      if (!payload.title) {
        toast.error('El título es obligatorio.');
        setSaving(false); return;
      }
      if (form.video_type === 'mp4' && !payload.video_url) {
        toast.error('URL MP4 obligatoria.');
        setSaving(false); return;
      }
      if (form.video_type === 'hls' && !payload.hls_url) {
        toast.error('URL HLS obligatoria.');
        setSaving(false); return;
      }
      if (form.video_type === 'embed' && !payload.embed_url) {
        toast.error('URL embed obligatoria.');
        setSaving(false); return;
      }
      if (form.video_type === 'external_link' && !payload.external_url) {
        toast.error('URL del contenido obligatoria.');
        setSaving(false); return;
      }

      if (form.id) {
        await adminUpdateVideo(form.id, payload);
        toast.success('Contenido actualizado.');
      } else {
        await adminCreateVideo(eventId, payload);
        toast.success('Contenido creado.');
      }
      setDrawerOpen(false);
      await reload();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (row) => {
    const next = ['published', 'replay_available'].includes(row.status) ? 'unavailable' : 'published';
    try {
      await adminUpdateVideo(row.id, { status: next });
      toast.success(next === 'published' ? 'Publicado.' : 'Desactivado.');
      await reload();
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  const handleMove = async (row, direction) => {
    try {
      await adminMoveVideo(row.id, direction);
      await reload();
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  const doDelete = async () => {
    if (!deleteId) return;
    try {
      await adminDeleteVideo(deleteId);
      toast.success('Contenido eliminado.');
      setDeleteId(null);
      await reload();
    } catch (err) {
      toast.error(friendlyError(err));
      setDeleteId(null);
    }
  };

  const handleThumbnailUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAdminImage(file, 'thumbnail');
      const url = res?.url || res?.data?.url;
      if (url) {
        setForm((f) => ({ ...f, thumbnail_url: url }));
        toast.success('Imagen subida.');
      }
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setUploading(false);
    }
  };

  // ------- Render --------
  return (
    <AdminLayout>
      <div className="max-w-6xl">
        {/* Cabecera */}
        <header className="mb-6">
          <nav className="text-sm text-[var(--ns-text-muted)] mb-2">
            <Link to="/admin/eventos" className="hover:text-[var(--ns-gold-700)]" data-testid="admin-content-breadcrumb-back">
              ← Partidos
            </Link>
            <span className="mx-2 text-[var(--ns-text-subtle)]">/</span>
            <span className="text-[var(--ns-text)]">{eventTitle}</span>
          </nav>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-[var(--ns-gold-700)] font-semibold">Contenidos</p>
              <h1 className="font-display text-2xl sm:text-3xl text-[var(--ns-text)] uppercase">{eventTitle}</h1>
              <p className="text-sm text-[var(--ns-text-muted)] mt-1">
                {dateLabel}{sportLabel ? ` · ${sportLabel}` : ''}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {event?.slug ? (
                <Button asChild variant="outline" className="h-10 rounded-xl">
                  <Link to={`/eventos/${event.slug}`} data-testid="admin-content-open-public">
                    <ExternalLink className="h-4 w-4 mr-2" /> Ver partido público
                  </Link>
                </Button>
              ) : null}
              <Button
                variant="outline"
                onClick={() => setDownloadTarget({ sourceType: 'event', sourceId: eventId, title: event?.title || 'Partido' })}
                className="h-10 rounded-xl border-[var(--ns-gold)]/40 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/15"
                data-testid="admin-event-contents-manage-event-download"
              >
                <Download className="h-4 w-4 mr-2" /> Gestionar descarga (Partido)
              </Button>
              <Button
                onClick={openNew}
                className="h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold border-0"
                data-testid="admin-event-contents-add"
              >
                <Plus className="h-4 w-4 mr-2" /> Añadir contenido
              </Button>
            </div>
          </div>
        </header>

        <p className="text-sm text-[var(--ns-text-muted)] mb-4">
          Contenidos del partido · <strong className="text-[var(--ns-text)]">{items.length}</strong>
        </p>

        {/* Listado de tarjetas */}
        {loading ? (
          <Card className="p-10 text-center text-[var(--ns-text-muted)]">Cargando…</Card>
        ) : items.length === 0 ? (
          <Card className="p-10 text-center" data-testid="admin-content-empty">
            <Film className="h-10 w-10 text-[var(--ns-text-subtle)] mx-auto mb-3" />
            <p className="text-[var(--ns-text-muted)] mb-4">Este partido aún no tiene contenidos.</p>
            <Button onClick={openNew} className="bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612]" data-testid="admin-event-contents-add-first">
              <Plus className="h-4 w-4 mr-2" /> Añadir el primer contenido
            </Button>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="admin-content-cards">
            {items.map((row, idx) => (
              <Card key={row.id} className="bg-white border border-[var(--ns-border)] overflow-hidden" data-testid={`admin-content-card-${row.id}`}>
                <div className="flex flex-col md:flex-row gap-4 p-4">
                  {/* Miniatura — Prioridad: subida específica > fija statistics > icono */}
                  <div className="md:w-52 w-full flex-shrink-0">
                    <div className="aspect-video rounded-xl overflow-hidden bg-[var(--ns-bg-2)] border border-[var(--ns-border)] relative">
                      {row.thumbnail_url ? (
                        <img src={buildAssetUrl(row.thumbnail_url)} alt={row.title} className="w-full h-full object-cover" />
                      ) : row.content_kind === 'statistics' ? (
                        <img
                          src={statsThumbnail}
                          alt={row.title || 'Estadísticas del partido'}
                          className="w-full h-full object-cover"
                          data-testid={`admin-content-stats-thumb-${row.id}`}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-[var(--ns-text-subtle)]">
                          <ImageIcon className="h-7 w-7" />
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-start gap-2 mb-2">
                      <h3 className="font-semibold text-[var(--ns-text)] text-base sm:text-lg flex-1 min-w-0">{row.title}</h3>
                      <Badge className={`text-[10px] uppercase ${STATUS_BADGE_CLASS[row.status] || 'bg-[var(--ns-bg-2)]'}`}>
                        {STATUS_LABEL[row.status] || row.status}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="px-2 py-1 rounded-md bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)]">
                        {CONTENT_KIND.find((k) => k.value === row.content_kind)?.label || row.content_kind}
                      </span>
                      <span className="px-2 py-1 rounded-md bg-[var(--ns-gold)]/10 text-[var(--ns-gold-700)]">
                        Acceso: {accessLabel(row)}
                      </span>
                      <span className="px-2 py-1 rounded-md bg-blue-50 text-blue-700">
                        Descarga: {downloadLabel(row)}
                      </span>
                    </div>
                    {row.description ? (
                      <p className="text-sm text-[var(--ns-text-muted)] mt-2 line-clamp-2">{row.description}</p>
                    ) : null}
                  </div>
                  {/* Acciones */}
                  <div className="flex md:flex-col items-stretch md:items-end gap-1.5 md:w-44">
                    {event?.slug ? (
                      <Button asChild variant="ghost" size="sm" className="h-8 px-2 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/15" data-testid={`admin-content-preview-${row.id}`}>
                        <Link to={`/eventos/${event.slug}/watch?v=${row.id}`} target="_blank">
                          <Eye className="h-3.5 w-3.5 mr-1" /> Vista previa
                        </Link>
                      </Button>
                    ) : null}
                    <Button variant="ghost" size="sm" onClick={() => openEdit(row)} className="h-8 px-2 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/15" data-testid={`admin-content-edit-${row.id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                    </Button>
                    {row.content_kind === 'highlights' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAssignVideoId(row.id)}
                        className="h-8 px-2 border-[var(--ns-gold)]/40 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/15"
                        data-testid={`admin-content-assign-players-${row.id}`}
                      >
                        <Users className="h-3.5 w-3.5 mr-1" /> Asignar jugadores
                      </Button>
                    ) : null}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDownloadTarget({ sourceType: 'video', sourceId: row.id, title: row.title })}
                      className="h-8 px-2 border-[var(--ns-gold)]/40 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/15"
                      data-testid={`admin-content-manage-download-${row.id}`}
                    >
                      <Download className="h-3.5 w-3.5 mr-1" /> Gestionar descarga
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setGrantsTarget({ videoId: row.id, videoTitle: row.title })}
                      className="h-8 px-2 border-[var(--ns-gold)]/40 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/15"
                      data-testid={`admin-content-manage-grants-${row.id}`}
                    >
                      <Users className="h-3.5 w-3.5 mr-1" /> Gestionar accesos gratuitos
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 px-2" data-testid={`admin-content-more-${row.id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        <DropdownMenuItem onClick={() => togglePublish(row)} data-testid={`admin-content-toggle-${row.id}`}>
                          <Power className="h-3.5 w-3.5 mr-2" />
                          {['published', 'replay_available'].includes(row.status) ? 'Desactivar' : 'Publicar'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleMove(row, 'up')}
                          disabled={idx === 0}
                          data-testid={`admin-content-move-up-${row.id}`}
                        >
                          <ArrowUp className="h-3.5 w-3.5 mr-2" /> Subir posición
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleMove(row, 'down')}
                          disabled={idx === items.length - 1}
                          data-testid={`admin-content-move-down-${row.id}`}
                        >
                          <ArrowDown className="h-3.5 w-3.5 mr-2" /> Bajar posición
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteId(row.id)}
                          className="text-red-600 focus:text-red-700 focus:bg-red-50"
                          data-testid={`admin-content-delete-${row.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-2" /> Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Drawer Añadir / Editar */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl bg-white overflow-y-auto border-l border-[var(--ns-border)]"
          data-testid="admin-content-drawer"
        >
          <SheetHeader>
            <SheetTitle>{form.id ? 'Editar contenido' : 'Añadir contenido'}</SheetTitle>
            <SheetDescription>
              Completa los datos visibles para los espectadores. El partido se asigna automáticamente.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Título <span className="text-red-500">*</span></Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Highlights del partido, entrevista al entrenador…"
                data-testid="admin-content-field-title" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.content_kind} onValueChange={(v) => setForm({ ...form, content_kind: v })}>
                <SelectTrigger data-testid="admin-content-field-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONTENT_KIND.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="admin-content-field-description" />
            </div>
            <div>
              <Label>Fuente de contenido</Label>
              <Select value={form.video_type} onValueChange={(v) => setForm({ ...form, video_type: v, video_url: '', hls_url: '', embed_url: '', external_url: '' })}>
                <SelectTrigger data-testid="admin-content-field-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCE.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.video_type === 'mp4' && (
              <div>
                <Label>URL MP4</Label>
                <Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                  placeholder="https://cdn.example.com/video.mp4"
                  data-testid="admin-content-field-url-mp4" />
              </div>
            )}
            {form.video_type === 'hls' && (
              <div>
                <Label>URL HLS .m3u8</Label>
                <Input value={form.hls_url} onChange={(e) => setForm({ ...form, hls_url: e.target.value })}
                  placeholder="https://streaming.example.com/playlist.m3u8"
                  data-testid="admin-content-field-url-hls" />
              </div>
            )}
            {form.video_type === 'embed' && (
              <div>
                <Label>URL embed</Label>
                <Input value={form.embed_url} onChange={(e) => setForm({ ...form, embed_url: e.target.value })}
                  placeholder="https://iframe.mediadelivery.net/embed/... o https://www.youtube-nocookie.com/embed/..."
                  data-testid="admin-content-field-url-embed" />
                <p className="text-xs text-[var(--ns-text-subtle)] mt-1">YouTube, Vimeo, Dailymotion o Bunny Stream/CDN.</p>
              </div>
            )}
            {form.video_type === 'external_link' && (
              <div>
                <Label>URL del contenido</Label>
                <Input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })}
                  placeholder="https://ejemplo.com/estadisticas-del-partido"
                  data-testid="admin-content-field-external-url" />
                <p className="text-xs text-[var(--ns-text-subtle)] mt-1">Cualquier página web https://…, no necesariamente un vídeo.</p>
              </div>
            )}

            {/* Imagen de portada — subida real */}
            <div>
              <Label>Imagen de portada (opcional)</Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="w-32 h-20 rounded-lg bg-[var(--ns-bg-2)] border border-[var(--ns-border)] overflow-hidden flex items-center justify-center">
                  {form.thumbnail_url ? (
                    <img src={buildAssetUrl(form.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-[var(--ns-text-subtle)]" />
                  )}
                </div>
                <div className="flex-1">
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--ns-border)] bg-white text-sm cursor-pointer hover:bg-[var(--ns-bg)]">
                    <Upload className="h-3.5 w-3.5" />
                    {uploading ? 'Subiendo…' : 'Subir imagen'}
                    <input
                      type="file" accept="image/*" className="hidden"
                      onChange={(e) => handleThumbnailUpload(e.target.files?.[0])}
                      disabled={uploading}
                      data-testid="admin-content-field-thumbnail-upload"
                    />
                  </label>
                  {form.thumbnail_url ? (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, thumbnail_url: '' })}
                      className="ml-2 text-xs text-red-600 hover:underline"
                    >
                      Quitar
                    </button>
                  ) : null}
                  <p className="text-xs text-[var(--ns-text-subtle)] mt-1">JPG o PNG, máx 5 MB.</p>
                </div>
              </div>
            </div>

            <div>
              <Label>Acceso al contenido</Label>
              <Select value={form.access_type} onValueChange={(v) => setForm({ ...form, access_type: v })}>
                <SelectTrigger data-testid="admin-content-field-access"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACCESS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isPriceVisible && (
              <div>
                <Label>Precio de reproducción</Label>
                <div className="relative">
                  <Input
                    type="text" inputMode="decimal"
                    value={form.price_eur}
                    onChange={(e) => setForm({ ...form, price_eur: e.target.value })}
                    placeholder="2,50"
                    data-testid="admin-content-field-price"
                    className="pr-9"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[var(--ns-text-muted)] pointer-events-none">€</span>
                </div>
              </div>
            )}

            <div>
              <Label>Configuración de descarga</Label>
              <Select value={form.download_mode} onValueChange={(v) => setForm({ ...form, download_mode: v })}>
                <SelectTrigger data-testid="admin-content-field-download"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOWNLOAD_MODES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isDownloadPriceVisible && (
              <div>
                <Label>Precio de descarga</Label>
                <div className="relative">
                  <Input
                    type="text" inputMode="decimal"
                    value={form.download_price_eur}
                    onChange={(e) => setForm({ ...form, download_price_eur: e.target.value })}
                    placeholder="1,50"
                    data-testid="admin-content-field-download-price"
                    className="pr-9"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[var(--ns-text-muted)] pointer-events-none">€</span>
                </div>
              </div>
            )}

            <div>
              <Label>Estado</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="admin-content-field-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="published">Publicado</SelectItem>
                  <SelectItem value="unavailable">Desactivado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setDrawerOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving}
              className="bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold border-0"
              data-testid="admin-content-drawer-submit">
              {saving ? 'Guardando…' : (form.id ? 'Guardar cambios' : 'Crear contenido')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este contenido?</AlertDialogTitle>
            <AlertDialogDescription>
              La eliminación es lógica y reversible mediante soporte. Si existen compras
              o accesos asociados, el sistema bloqueará el borrado y deberás cambiar su
              estado a Desactivado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Fase 09 — Diálogo de asignación de jugadores a un highlight oficial */}
      <AssignPlayersDialog
        open={!!assignVideoId}
        onOpenChange={(o) => { if (!o) setAssignVideoId(null); }}
        videoId={assignVideoId}
        eventId={eventId}
        onSaved={reload}
      />

      {/* Fase 06D — Gestión de download_assets sobre event | video */}
      <AdminDownloadAssetDialog
        open={!!downloadTarget}
        onOpenChange={(o) => { if (!o) setDownloadTarget(null); }}
        sourceType={downloadTarget?.sourceType}
        sourceId={downloadTarget?.sourceId}
        sourceTitle={downloadTarget?.title}
      />

      {/* Fase 1 — Concesiones gratuitas de visualización por vídeo */}
      <AdminVideoGrantsDialog
        open={!!grantsTarget}
        onOpenChange={(o) => { if (!o) setGrantsTarget(null); }}
        videoId={grantsTarget?.videoId}
        videoTitle={grantsTarget?.videoTitle}
      />
    </AdminLayout>
  );
}
